import { ValidityState, getInitializedValidityState, addError } from "js/types";
import {
    Contents,
    ContentSources,
    DeleteRequest,
    ContentSourceType
} from "js/models";
import { S3Service } from "js/services";
import { useState, useMemo } from "react";
export interface FileManagerHook {
    items: Contents[];
    updatedItem: Contents;
    validityState: ValidityState;
    loading: boolean;
    toContents: (identifier: string) => Contents;
    loadContent: () => Promise<Contents[]>;
    upload: (
        file: File,
        path: string,
        onProgressUpdate: (progress: number) => void
    ) => Promise<Contents>;
    createFolder: (name: string, prefix?: string) => Promise<Contents>;
    getThumbnailUrl: (identifier: string) => string;
    renameDirectory: (identifier: string, newName: string) => Promise<Contents>;
    //rename: (identifier: string, newFileName: string) => Promise<Contents>;
    deleteItems: (content: Contents[]) => Promise<Contents[]>;
}

/**
 *
 * @param parent "" for bucket root
 */
export function useS3(
    contentSource: ContentSources,
    parent: string,
    params: {
        root: string;
        generatedFilesRoot: string;
    }
): FileManagerHook {
    const s3Service = useMemo(() => {
        return new S3Service(contentSource, params);
    }, []);
    const [items, setItems] = useState<Contents[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [validityState, setValidityState] = useState<ValidityState>(null);
    const [updatedItem, setUpdatedItem] = useState<Contents>(null);

    const resetValidityState = () => {
        setValidityState(getInitializedValidityState([], []));
    };

    const setValidityStateFromError = (error: any) => {
        setValidityState(
            addError(validityState, error.name, {
                code: error.code,
                message: error.message
            })
        );
    };

    const getParent = (p: string) => {
        return p ? p : parent;
    };

    const loadContent = () => {
        setLoading(true);
        resetValidityState();
        return s3Service
            .getListings(parent)
            .then(resp => {
                setItems(resp);
                return resp;
            })
            .catch(err => {
                console.error(err);
                setValidityStateFromError(err);
                return [];
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const createFolder = (name: string, parent?: string) => {
        setLoading(true);
        resetValidityState();
        return s3Service
            .createDirectory(name, getParent(parent))
            .then(resp => {
                setUpdatedItem(resp);
                return resp;
            })
            .catch(err => {
                console.error(err);
                setValidityStateFromError(err);
                return null;
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const renameDirectory = (identifier: string, newName: string) => {
        setLoading(true);
        const parent = S3Service.getParent(identifier);
        const newKey = `${parent}${newName}${
            identifier.endsWith("/") ? "/" : ""
        }`;
        resetValidityState();
        return s3Service
            .rename(identifier, newKey)
            .then(resp => {
                setUpdatedItem(resp);
                return resp;
            })
            .catch(err => {
                console.error(err);
                setValidityStateFromError(err);
                return null;
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const deleteItems = (content: Contents[]) => {
        resetValidityState();
        setLoading(true);
        const toDel = content.map(a => {
            return {
                key: a.identifier,
                type: a.mimeType === null ? "folder" : "file"
            } as DeleteRequest;
        });
        return s3Service
            .bulkDelete(toDel)
            .then(resp => {
                return content;
            })
            .catch(err => {
                console.error(err);
                setValidityStateFromError(err);
                return null;
            })
            .finally(() => {
                setLoading(false);
            });
    };
    const upload = (
        file: File,
        path: string,
        onProgressUpdate: (progress: number) => void
    ) => {
        resetValidityState();
        return s3Service
            .uploadFile(file, getParent(path), onProgressUpdate)
            .then(resp => {
                setUpdatedItem(resp);
                return resp;
            })
            .catch(err => {
                console.error(err);
                setValidityStateFromError(err);
                return null;
            });
    };

    const toContents = (identifier: string): Contents => {
        return {
            ContentSourceType: ContentSourceType.S3Bucket,
            relPath: identifier,
            identifier,
            name: S3Service.getName(identifier),
            url: s3Service.getFullUrl(identifier),
            fullData: null,
            createdAt: null,
            mimeType: null,
            modifiedAt: null,
            metadata: null,
            size: null
        };
    };

    return {
        toContents,
        items,
        updatedItem,
        getThumbnailUrl: (key: string) => s3Service.getThumbnailUrl(key),
        loading,
        validityState,
        upload,
        renameDirectory,
        deleteItems,
        createFolder,
        loadContent
    };
}
